import * as isNumber from 'lodash/isNumber'
import { connectShadedLoadable, createParallelGuard } from 'helpers/loadableHelpers'
import { MoneyflowTrendOwnProps } from 'interfaces/moneyflow'
import { Guards } from 'interfaces/guards'
import { peergroupsGuard } from 'guards/globalGuards'
import { moneyflowTrackersGuard, memberMoneyflowTrendGuard, peergroupMoneyflowTrendsGuard, memberOppositeMoneyflowTrendsGuard } from 'guards/moneyflowGuards'
import MonthlyBarChart, { MonthlyBarChartProps } from 'components/features/pfm/MonthlyBarChart/MonthlyBarChart'
import LineChartLoadingOverlay from 'components/atoms/preloaders/LineChartLoadingOverlay'
import { yourPeergroupIdSelector, selectedPeergroupIdSelector, selectedRelationSelector } from 'selectors/peergroupSelectors'
import { LineChartInnerOwnProps } from 'components/features/pfm/LineChart/LineChart'
import { createForgottenGuardError } from 'helpers/errorHelpers'
import {
  memberMoneyflowTrendsSelector,
  yourPeersMoneyflowTrendsSelector,
  othersMoneyflowTrendsSelector,
  memberMoneyflowComparisonTrendsSelector,
  moneyflowTrackerLimitSelector,
} from 'selectors/moneyflowSelectors'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { SecondaryLine } from 'interfaces/chart'
import { createSelector } from 'reselect'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

type OwnProps =
  & MoneyflowTrendOwnProps
  & LineChartInnerOwnProps

const mapStateToProps = () => createSelector(
  createOwnPropsSelector<OwnProps>(),
  yourPeergroupIdSelector,
  selectedPeergroupIdSelector,
  moneyflowTrackerLimitSelector,
  memberMoneyflowTrendsSelector,
  yourPeersMoneyflowTrendsSelector,
  othersMoneyflowTrendsSelector,
  selectedRelationSelector,
  memberMoneyflowComparisonTrendsSelector,
  isMobileSelector,
  withComparisonSelector,
  (ownProps, yourPeergroupId, selectedPeergroupId, trackerLimit, memberTrend, peersTrend, othersTrend, selectedRelation, comparisonTrend, isMobile, withComparison) => {
    if (!isNumber(yourPeergroupId) || !isNumber(selectedPeergroupId)) {
      throw createForgottenGuardError('MoneyflowMonthlyTrendContainer', 'peergroupsGuard')
    }

    const { type } = ownProps

    if (!peersTrend || !othersTrend || !memberTrend) {
      throw createForgottenGuardError('MoneyflowMonthlyTrendContainer', 'peergroupMoneyflowTrendsGuard')
    }

    if (!selectedRelation) {
      throw createForgottenGuardError('MoneyflowMonthlyTrendContainer', 'peergroupsGuard')
    }

    const compareLine: SecondaryLine | undefined = comparisonTrend ? {
      title: type === 'spending' ? 'Your Avg. Income:' : 'Your Avg. Spending:',
      amount: comparisonTrend.average,
    } : undefined

    return {
      memberActualValues: memberTrend.actual,
      memberProjectedValue: memberTrend.projected
        ? memberTrend.projected[0] // there should be only one
        : undefined,
      peerActualValues: peersTrend.actual,
      peerProjectedValue: peersTrend.projected
        ? peersTrend.projected[0] // there should be only one
        : undefined,
      otherActualValues: othersTrend.actual,
      otherProjectedValue: othersTrend.projected
        ? othersTrend.projected[0] // there should be only one
        : undefined,
      compareLine,
      limitLine: trackerLimit ? trackerLimit : undefined,
      isMobile,
      selectedPeergroupName: selectedRelation.name,
      withComparison,
    }
  }
)

const guards: Guards<OwnProps> = [
  peergroupsGuard,
  createParallelGuard(
    memberMoneyflowTrendGuard, peergroupMoneyflowTrendsGuard,
    memberOppositeMoneyflowTrendsGuard, moneyflowTrackersGuard,
  ),
]

export default connectShadedLoadable<
  MonthlyBarChartProps,
  {},
  LineChartInnerOwnProps,
  OwnProps
>(mapStateToProps, undefined, guards, LineChartLoadingOverlay)(MonthlyBarChart)
